import { Formik, Form, Field } from 'formik'
import type { FieldProps } from 'formik'
import { Box, Button, Group, Stack, Text, Textarea, Title, ActionIcon } from '@mantine/core'
import { IconCheck, IconX, IconArrowLeft } from '@tabler/icons-react'
import CategoryDropdown from './CategoryDropdown'
import { ImageUpload } from '../ui'
import { useEffect, useState } from 'react'

interface SpottingFormProps {
    location: { lat: number; lng: number }
    onSubmit: (data: FormData) => void | Promise<void>
    onCancel: () => void
    isLoading?: boolean
    initialData?: {
        description: string
        categoryId?: string
    }
    isEditing?: boolean
}

interface FormValues {
    description: string
    categoryId: string
    image: File | null
}

export function SpottingForm({
    location,
    onSubmit,
    onCancel,
    isLoading = false,
    initialData,
    isEditing = false
}: SpottingFormProps) {
    const [address, setAddress] = useState<string>('')

    const initialValues: FormValues = {
        description: initialData?.description || '',
        categoryId: initialData?.categoryId || '',
        image: null,
    }

    // Reverse geocoding to get address from coordinates
    useEffect(() => {
        if (window.google && window.google.maps && location) {
            const geocoder = new window.google.maps.Geocoder()
            const latlng = { lat: location.lat, lng: location.lng }

            geocoder.geocode({ location: latlng }, (results, status) => {
                console.log('Geocoding status:', status, 'Results:', results)
                if (status === 'OK' && results && results[0]) {
                    setAddress(results[0].formatted_address)
                } else {
                    console.error('Geocoding failed:', status)
                    setAddress('Address lookup failed')
                }
            })
        }
    }, [location])

    const handleSubmit = async (values: FormValues) => {
        if (!values.categoryId) {
            return // Basic validation
        }

        const formData = new FormData();
        formData.append('description', values.description.trim());
        formData.append('categoryId', values.categoryId);
        formData.append('latitude', location.lat.toString());
        formData.append('longitude', location.lng.toString());
        if (address) {
            formData.append('address', address);
        }
        if (values.image) {
            console.log(`adding image ${values.image}`)
            formData.append('image', values.image);
        }

        try {
            await onSubmit(formData);
        } catch (error) {
            console.error('Error submitting spotting:', error);
        }
    }

    // Add ESC key listener
    useEffect(() => {
        console.log('adding esc listener')
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onCancel();
            }
        };

        window.addEventListener('keydown', handleEsc);

        return () => {
            window.removeEventListener('keydown', handleEsc);
        };
    }, [onCancel]);

    return (
        <Box className="h-full">
            <Stack gap="md" className="h-full">
                <div>
                    <Group align="center" gap="sm" mb="xs">
                        <ActionIcon
                            variant="subtle"
                            size="lg"
                            onClick={onCancel}
                            aria-label="Go back"
                        >
                            <IconArrowLeft size={20} />
                        </ActionIcon>
                        <Title order={3} size="h4">
                            {isEditing ? 'Edit Spotting' : 'Add New Spotting'}
                        </Title>
                    </Group>
                    <Text size="sm" c="dimmed">
                        Location: {location.lat.toFixed(6)}, {location.lng.toFixed(6)}
                    </Text>
                    {address && (
                        <Text size="sm" c="dimmed">
                            Address: {address}
                        </Text>
                    )}
                    <Text size="xs" c="dimmed" mt="xs">
                        Press Esc to cancel
                    </Text>
                </div>

                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={false}
                    validateOnChange={false}
                    validateOnBlur={false}
                    validateOnMount={false}
                >
                    {({ values, isSubmitting }) => (
                        <Form className="flex-1 flex flex-col">
                            <Stack gap="md" className="flex-1">
                                <Field name="image">
                                    {({ field, form }: FieldProps) => (
                                        <ImageUpload
                                            value={field.value}
                                            onChange={(file) => form.setFieldValue(field.name, file)}
                                        />
                                    )}
                                </Field>

                                <Field name="categoryId">
                                    {() => (
                                        <CategoryDropdown

                                        />
                                    )}
                                </Field>

                                <Field name="description">
                                    {({ field }: FieldProps) => (
                                        <Textarea
                                            {...field}
                                            label="Description"
                                            placeholder="Tell us more about this spotting..."
                                            minRows={3}
                                            autosize
                                        />
                                    )}
                                </Field>
                            </Stack>

                            <Group justify="flex-end" mt="md">
                                <Button
                                    variant="subtle"
                                    leftSection={<IconX size={16} />}
                                    onClick={onCancel}
                                    disabled={isLoading || isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    leftSection={<IconCheck size={16} />}
                                    loading={isLoading || isSubmitting}
                                    disabled={!values.categoryId}
                                >
                                    {isEditing ? 'Update Spotting' : 'Add Spotting'}
                                </Button>
                            </Group>
                        </Form>
                    )}
                </Formik>
            </Stack>
        </Box>
    )
} 